import { useState, memo, useEffect, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import type { Hit, HighlightedCellsWithQuarter, NeighborConfig, BoxNeighborConfig, NeighborDirection, NeighborPayoutEntry } from '../../types';
import { getNeighborPositions, getBoxNeighborPositions, CARDINAL_DIRECTIONS, ALL_NEIGHBOR_DIRECTIONS, NEIGHBOR_DIRECTION_LABELS } from '../../types';
import { ScoreInputForm } from './ScoreInputForm';

// Helper to get owner display name from either owner or offline player
// eslint-disable-next-line react-refresh/only-export-components
export const getSquareOwnerName = (
  square?: { owner?: { displayName: string } | null; offlinePlayer?: { displayName: string } | null }
): string => {
  if (!square) return 'Unclaimed';
  return square.owner?.displayName || square.offlinePlayer?.displayName || 'Unclaimed';
};

// Helper to get neighbor payout owner name
const getNeighborOwnerName = (payout: NeighborPayoutEntry): string => {
  return payout.user?.displayName || payout.offlinePlayer?.displayName || 'Unclaimed';
};

export interface HitCardProps {
  // Display
  title: string;
  titleTooltip?: string;
  subheaderContent?: ReactNode;

  // Hit data
  hit?: Hit;
  wrongWayHit?: Hit;
  plusNHit?: Hit;
  plusNWrongWayHit?: Hit;
  quarter?: number; // Quarter number for multi-number mode highlighting

  // Payout configuration for highlighting
  neighborConfig?: NeighborConfig;
  boxNeighborConfig?: BoxNeighborConfig;

  // State
  isEditing: boolean;
  isCurrentUserWinner?: boolean;

  // Permissions
  canEdit: boolean;
  canRecord?: boolean; // For quarters that need previous recorded first
  showDelete?: boolean;

  // Team info
  homeTeam: string;
  awayTeam: string;

  // Default scores (for editing new entries)
  defaultHomeScore?: number;
  defaultAwayScore?: number;

  // Display options
  terseMode?: boolean;

  // Callbacks
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveScore: (homeScore: number, awayScore: number) => Promise<void>;
  onDeleteHit?: () => void;
  onHitHover: (data: HighlightedCellsWithQuarter | null) => void;
}

export const HitCard = memo<HitCardProps>(({
  title,
  titleTooltip,
  subheaderContent,
  hit,
  wrongWayHit,
  plusNHit,
  plusNWrongWayHit,
  quarter,
  neighborConfig,
  boxNeighborConfig,
  isEditing,
  isCurrentUserWinner = false,
  canEdit,
  canRecord = true,
  showDelete = false,
  homeTeam,
  awayTeam,
  defaultHomeScore = 0,
  defaultAwayScore = 0,
  terseMode = false,
  onStartEditing,
  onCancelEditing,
  onSaveScore,
  onDeleteHit,
  onHitHover,
}) => {
  const [homeValue, setHomeValue] = useState(hit?.homeScore ?? defaultHomeScore);
  const [awayValue, setAwayValue] = useState(hit?.awayScore ?? defaultAwayScore);
  const [saving, setSaving] = useState(false);

  // Reset values when editing state changes
  useEffect(() => {
    if (isEditing) {
      setHomeValue(hit?.homeScore ?? defaultHomeScore);
      setAwayValue(hit?.awayScore ?? defaultAwayScore);
    }
  }, [isEditing, hit, defaultHomeScore, defaultAwayScore]);

  // Accept optional override values for when Enter is pressed before blur
  const handleSave = async (overrideHome?: number, overrideAway?: number) => {
    setSaving(true);
    try {
      const finalHome = overrideHome ?? homeValue;
      const finalAway = overrideAway ?? awayValue;
      await onSaveScore(finalHome, finalAway);
    } finally {
      setSaving(false);
    }
  };

  // Build highlight data with cells and quarter context (for multi-number mode)
  const getHighlightData = (): HighlightedCellsWithQuarter | null => {
    if (!hit || hit.winningRow === undefined || hit.winningCol === undefined) {
      return null;
    }
    const cells: { row: number; col: number }[] = [{ row: hit.winningRow, col: hit.winningCol }];

    // Add wrong way hit cell
    if (wrongWayHit && wrongWayHit.winningRow !== undefined && wrongWayHit.winningCol !== undefined) {
      cells.push({ row: wrongWayHit.winningRow, col: wrongWayHit.winningCol });
    }

    // Add plus N hit cell
    if (plusNHit && plusNHit.winningRow !== undefined && plusNHit.winningCol !== undefined) {
      cells.push({ row: plusNHit.winningRow, col: plusNHit.winningCol });
    }

    // Add plus N wrong way hit cell
    if (plusNWrongWayHit && plusNWrongWayHit.winningRow !== undefined && plusNWrongWayHit.winningCol !== undefined) {
      cells.push({ row: plusNWrongWayHit.winningRow, col: plusNWrongWayHit.winningCol });
    }

    // Add neighbor cells if neighbor payouts are enabled (directional neighbors)
    if (neighborConfig?.enabled) {
      // Determine which directions to use
      let directions: NeighborDirection[];
      if (neighborConfig.directions && neighborConfig.directions.length > 0) {
        directions = neighborConfig.directions;
      } else if (neighborConfig.type === 'box') {
        // Legacy: box type means all 8 directions
        directions = ALL_NEIGHBOR_DIRECTIONS;
      } else {
        // Legacy: adjacent/nearest type means cardinal directions
        directions = CARDINAL_DIRECTIONS;
      }

      // Get neighbor positions with wrap-around
      const neighborPositions = getNeighborPositions(hit.winningRow, hit.winningCol, directions);
      for (const pos of neighborPositions) {
        cells.push({ row: pos.row, col: pos.col });
      }
    }

    // Add box neighbor cells if box neighbor payouts are enabled
    if (boxNeighborConfig?.enabled && boxNeighborConfig.boxSize) {
      const boxNeighborPositions = getBoxNeighborPositions(hit.winningRow, hit.winningCol, boxNeighborConfig.boxSize);
      for (const pos of boxNeighborPositions) {
        cells.push({ row: pos.row, col: pos.col });
      }
    }

    return { cells, quarter };
  };

  const neighborPayoutTotal = hit?.neighborPayouts?.reduce((sum, p) => sum + p.amount, 0) ?? 0;
  const totalPayout = hit
    ? hit.payoutAmount + (wrongWayHit?.payoutAmount ?? 0) + (plusNHit?.payoutAmount ?? 0) + (plusNWrongWayHit?.payoutAmount ?? 0) + neighborPayoutTotal
    : 0;

  // Determine if we need CardContent
  const hasContent = isEditing || hit;

  // Winner highlight styles
  const winnerStyles = isCurrentUserWinner ? {
    background: 'linear-gradient(135deg, #fff9c4 0%, #ffecb3 50%, #fff9c4 100%)',
    border: '2px solid',
    borderColor: '#ffc107',
    boxShadow: '0 0 8px rgba(255, 193, 7, 0.4)',
    '&:hover': { boxShadow: '0 0 12px rgba(255, 193, 7, 0.6)' },
  } : {};

  // Build action content based on state
  const renderAction = () => {
    if (isEditing) return undefined;

    if (hit) {
      if (totalPayout > 0) {
        return (
          <Chip
            label={`$${Math.round(totalPayout)}`}
            size="small"
            color="success"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
      return undefined;
    }

    // No hit state
    return canEdit ? (
      <Button size="small" variant="outlined" onClick={onStartEditing} disabled={!canRecord}>
        Record
      </Button>
    ) : undefined;
  };

  // Build subheader for no-hit state
  const renderSubheader = () => {
    if (isEditing || hit) return undefined;
    return subheaderContent;
  };

  // Render title with optional tooltip
  const renderTitle = () => {
    const titleContent = (
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 'bold',
          color: hit ? undefined : 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
    );

    if (titleTooltip) {
      return (
        <Tooltip title={titleTooltip} enterDelay={500}>
          {titleContent}
        </Tooltip>
      );
    }

    return titleContent;
  };

  return (
    <Card
      variant="outlined"
      onMouseEnter={hit ? () => {
        const data = getHighlightData();
        if (data) onHitHover(data);
      } : undefined}
      onMouseLeave={hit ? () => onHitHover(null) : undefined}
      onClick={hit && canEdit && !isEditing ? onStartEditing : undefined}
      sx={{
        bgcolor: hit ? 'success.50' : 'grey.50',
        borderColor: hit ? 'success.200' : undefined,
        cursor: hit && canEdit && !isEditing ? 'pointer' : 'default',
        '&:hover': hit && !isCurrentUserWinner && !isEditing ? { boxShadow: 2 } : {},
        ...winnerStyles,
      }}
    >
      <CardHeader
        title={renderTitle()}
        action={renderAction()}
        subheader={renderSubheader()}
        sx={{
          pb: hasContent ? 0 : undefined,
          '& .MuiCardHeader-content': { overflow: 'hidden' },
        }}
      />

      {hasContent && (
        <CardContent sx={{ pt: 1, '&:last-child': { pb: 1.5 } }}>
          {isEditing ? (
            <ScoreInputForm
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              homeValue={homeValue}
              awayValue={awayValue}
              onHomeChange={setHomeValue}
              onAwayChange={setAwayValue}
              onSave={handleSave}
              onCancel={onCancelEditing}
              onDelete={onDeleteHit}
              saving={saving}
              showDelete={showDelete && !!hit}
            />
          ) : hit ? (
            <>
              {/* Away - Home order to match NFL broadcasts */}
              <Typography variant="body2" sx={{ mb: terseMode ? 0 : 0.5 }}>
                {awayTeam} {hit.awayScore} - {homeTeam} {hit.homeScore}
              </Typography>
              {!terseMode && (
                <Table size="small" sx={{ '& td': { border: 0, py: 0, px: 0.5 }, '& td:first-of-type': { pl: 0 }, '& td:last-of-type': { pr: 0 } }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {getSquareOwnerName(hit.winningSquare)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          {hit.payoutAmount > 0 ? `$${Math.round(hit.payoutAmount)}` : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {wrongWayHit && (
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label="WW" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                            <Typography variant="caption" color="text.secondary">
                              {getSquareOwnerName(wrongWayHit.winningSquare)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {wrongWayHit.payoutAmount > 0 ? `$${Math.round(wrongWayHit.payoutAmount)}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {plusNHit && (
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label="+N" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                            <Typography variant="caption" color="text.secondary">
                              {getSquareOwnerName(plusNHit.winningSquare)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {plusNHit.payoutAmount > 0 ? `$${Math.round(plusNHit.payoutAmount)}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {plusNWrongWayHit && (
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label="+N WW" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                            <Typography variant="caption" color="text.secondary">
                              {getSquareOwnerName(plusNWrongWayHit.winningSquare)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {plusNWrongWayHit.payoutAmount > 0 ? `$${Math.round(plusNWrongWayHit.payoutAmount)}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {hit.neighborPayouts && hit.neighborPayouts.length > 0 && hit.neighborPayouts.map((payout, idx) => (
                      <TableRow key={`neighbor-${idx}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={NEIGHBOR_DIRECTION_LABELS[payout.direction as NeighborDirection] || payout.direction}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {getNeighborOwnerName(payout)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {payout.amount > 0 ? `$${Math.round(payout.amount)}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : null}
        </CardContent>
      )}

      {isEditing && (
        <CardActions sx={{ display: 'none' }}>
          {/* Actions are now in ScoreInputForm */}
        </CardActions>
      )}
    </Card>
  );
});

HitCard.displayName = 'HitCard';

export default HitCard;
